/**
 * 1 本の広告の数字（/money/ads/:id・2026-10-22 money/self-serve-ads-dash）。設計 §6 ④ の右側・§5「広告主に回数とゲームごとの割合を見せる」。
 *
 * 見せるもの（約束 §2 GET campaigns/:id）: 今日と全部の「表示・押された・使った額」・残り（今日 / 総額）・直近 7 日の棒・出たゲームの割合。
 * できること: 止める / 再開（約束の決まりどおり selfAds.canPause / canResume）・上限を変える（いつでも。下げた分は明日から）・
 *   断られた / 作りかけ → 作る画面の続きへ。
 * 今日の「使った額」は見込み（サーバーが floor(views × 値段 / 1000) で出す）。引くのは日の終わり（UTC）= そう書く。
 * 1 分ごとに読み直す（配信中の数字が動く）。遊んだ人の情報は出さない（約束がそもそも返さない）。
 */
import { useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import type { ApiError } from '../../api';
import { useResource } from '../../hooks';
import { errorText, formatPercent, useT } from '../../i18n';
import { money } from '../../money';
import { Empty, ErrorState, PageHeader, Spinner, StatTile, num } from '../../components/ui';
import { AdBanner } from '../../components/AdBanner';
import { campaignPath, isOff, pauseCampaign, resumeCampaign, SETTINGS_PATH, updateCampaign } from '../../selfAdsApi';
import {
  ADS_PATH,
  FALLBACK_SETTINGS,
  adResumePath,
  canChangeCaps,
  canEditCreative,
  canPause,
  canResume,
  capProblem,
  clickRate,
  dailyCapOptions,
  isKnownReason,
  lastSevenDays,
  statusWhy,
  todayLeftCents,
  totalCapOptions,
  totalLeftCents,
  utcToday,
  viewsFor,
  type CampaignDetail,
  type SelfAdSettings
} from '../../selfAds';
import { StatusPill } from './AdsListPage';
import { SSA_ERRORS } from './AdsNewPage';

export function AdDetailPage() {
  const { id = '' } = useParams();
  const location = useLocation();
  const { t } = useT();
  const r = useResource<CampaignDetail>(campaignPath(id), 60_000);
  const settings = useResource<SelfAdSettings>(SETTINGS_PATH);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const submitted = (location.state as { submitted?: boolean } | null)?.submitted === true;

  if (isOff(r.error) && r.error?.code !== 'campaign_not_found') {
    return (
      <Empty title={t('ssa.off.title')} icon="soon">
        <p>{t('ssa.off.body')}</p>
      </Empty>
    );
  }
  if (r.loading && !r.data) return <Spinner />;
  if (r.error || !r.data) {
    return r.error ? <ErrorState error={r.error} what={t('ssa.title')} onRetry={r.reload} /> : null;
  }

  const { campaign: c, today, total, days, hosts } = r.data;
  const why = statusWhy(c);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      r.reload();
    } catch (e) {
      setErr(errorText(e as ApiError, SSA_ERRORS));
    } finally {
      setBusy(false);
    }
  };

  const totalLeft = totalLeftCents(c);
  const shown = ['active', 'paused', 'stopped', 'ended'].includes(c.status);
  return (
    <>
      <PageHeader eyebrow={t('ssa.title')} title={c.targetName}>
        <Link to={ADS_PATH} className="btn btn-ghost btn-sm">
          {t('ssa.detail.all')}
        </Link>
      </PageHeader>

      {submitted && c.status === 'reviewing' && (
        <p className="alert alert-ok" role="status">
          {t('ssa.detail.sent')}
        </p>
      )}

      <section className="card ssa-status">
        <div className="card-head">
          <h2 className="card-title">{t('ssa.detail.status')}</h2>
          <StatusPill c={c} />
        </div>
        <p>{t(`ssa.why.${why}`)}</p>
        {c.status === 'rejected' && c.rejectReason && (
          <p className="small">
            <strong>{t('ssa.detail.reason')}</strong> {isKnownReason(c.rejectReason) ? t(`ssa.reason.${c.rejectReason}`) : c.rejectReason}
          </p>
        )}
        {c.status === 'stopped' && c.stopReason && (
          <p className="small">
            <strong>{t('ssa.detail.reason')}</strong> {c.stopReason}
          </p>
        )}
        <div className="form-actions">
          {canPause(c) && (
            <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => void act(() => pauseCampaign(c.id))}>
              {t('ssa.detail.pause')}
            </button>
          )}
          {canResume(c) && (
            <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void act(() => resumeCampaign(c.id))}>
              {t('ssa.detail.resume')}
            </button>
          )}
          {canEditCreative(c) && (
            <Link to={adResumePath(c.id)} className="btn btn-primary">
              {t(c.status === 'draft' ? 'ssa.row.continue' : 'ssa.row.fix')}
            </Link>
          )}
          {c.status === 'paused' && c.pauseReason === 'insufficient_credits' && (
            <Link to="/money/prepaid" className="btn btn-ghost">
              {t('ssa.budget.addFunds')}
            </Link>
          )}
        </div>
        {err && <p className="alert alert-error">{err}</p>}
      </section>

      {/* 数字は出たことのある広告だけ（確認中・断られた・作りかけで 0 や見込みが並ぶと「出て引かれた」と読める・見た目の確認 2026-10-22） */}
      {shown && (
        <>
      <h2 className="section-title">{t('ssa.detail.today')}</h2>
      <div className="tiles ssa-tiles">
        <StatTile label={t('ssa.stat.views')} value={num(today.views)} />
        <StatTile label={t('ssa.stat.clicks')} value={num(today.clicks)} hint={rateHint(today)} />
        <StatTile label={t('ssa.stat.spent')} value={money(today.chargedCents, 'usd')} hint={t('ssa.stat.spentTodayHint')} />
        <StatTile label={t('ssa.stat.leftToday')} value={money(todayLeftCents(c.dailyCapCents, today.chargedCents), 'usd')} hint={t('ssa.stat.ofDaily', { amount: money(c.dailyCapCents, 'usd') })} />
      </div>

      <h2 className="section-title">{t('ssa.detail.total')}</h2>
      <div className="tiles ssa-tiles">
        <StatTile label={t('ssa.stat.views')} value={num(total.views)} />
        <StatTile label={t('ssa.stat.clicks')} value={num(total.clicks)} hint={rateHint(total)} />
        {/* 累計は引いた（確定した）額だけ。今日の見込みは日の終わりに足される = そう書く（今日 $1.24・累計 $0.00 がずれて見えた・見た目の確認 2026-10-22） */}
        <StatTile
          label={t('ssa.stat.charged')}
          value={money(c.spentCents, 'usd')}
          hint={today.chargedCents > 0 ? t('ssa.stat.chargedHint', { amount: money(today.chargedCents, 'usd') }) : undefined}
        />
        <StatTile
          label={t('ssa.stat.leftTotal')}
          value={totalLeft === null ? t('ssa.stat.noLimit') : money(totalLeft, 'usd')}
          hint={totalLeft === null ? undefined : t('ssa.stat.ofTotal', { amount: money(c.totalCapCents ?? 0, 'usd') })}
        />
      </div>

      <div className="money-grid ssa-grid">
        <section className="card">
          <h2 className="card-title">{t('ssa.detail.week')}</h2>
          <WeekBars days={days} />
        </section>
        <section className="card">
          <h2 className="card-title">{t('ssa.detail.where')}</h2>
          {hosts.length === 0 ? (
            <p className="muted small">{t('ssa.detail.whereNone')}</p>
          ) : (
            <ul className="bar-list">
              {hosts.map((h) => (
                <li key={h.projectId}>
                  <div className="bar-list-row">
                    <span>{h.name}</span>
                    <span className="mono">{formatPercent(h.share)}</span>
                  </div>
                  <div className="meter" aria-hidden="true">
                    <span style={{ width: `${Math.min(100, Math.round(h.share * 100))}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          )}
          <p className="muted small">{t('ssa.detail.whereNote')}</p>
        </section>
      </div>

        </>
      )}

      <section className="card">
        <div className="card-head">
          <h2 className="card-title">{t('ssa.detail.limits')}</h2>
          {canChangeCaps(c) && !editing && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(true)}>
              {t('ssa.detail.changeLimits')}
            </button>
          )}
        </div>
        {editing ? (
          <LimitsForm
            settings={settings.data}
            detail={r.data}
            onCancel={() => setEditing(false)}
            onSave={(daily, totalCap) =>
              act(async () => {
                await updateCampaign(c.id, { dailyCapCents: daily, totalCapCents: totalCap });
                setEditing(false);
              })
            }
            busy={busy}
          />
        ) : (
          <dl className="kv ssa-budget-kv">
            <dt>{t('ssa.budget.daily')}</dt>
            <dd>{t('ssa.budget.option', { amount: money(c.dailyCapCents, 'usd'), views: viewsFor(c.dailyCapCents, c.priceCentsPerMille) })}</dd>
            <dt>{t('ssa.budget.total')}</dt>
            <dd>{c.totalCapCents === null ? t('ssa.budget.noLimit') : money(c.totalCapCents, 'usd')}</dd>
            <dt>{t('ssa.budget.price')}</dt>
            <dd>{t('ssa.budget.priceValue', { price: money(c.priceCentsPerMille, 'usd') })}</dd>
            {settings.data && (
              <>
                <dt>{t('ssa.budget.balance')}</dt>
                <dd>{money(settings.data.spendableCents, 'usd')}</dd>
              </>
            )}
          </dl>
        )}
      </section>

      <section className="card">
        <h2 className="card-title">{t('ssa.detail.ad')}</h2>
        <div className="ad-banners">
          <AdBanner campaignId={c.id} size={320} assetId={c.banner320AssetId} tagline={c.tagline} name={c.targetName} />
          <AdBanner campaignId={c.id} size={728} assetId={c.banner728AssetId} tagline={c.tagline} name={c.targetName} />
        </div>
        <p className="small muted">
          {t('ssa.detail.goesTo')} <span className="mono">{c.targetUrl}</span>
        </p>
      </section>
    </>
  );

  function rateHint(s: { views: number; clicks: number }) {
    const rate = clickRate(s);
    return rate === null ? undefined : t('ssa.stat.rate', { rate: formatPercent(rate) });
  }
}

/** 直近 7 日の表示の棒（手描きの SVG・グラフの部品は入れない方針 = README）。下に UTC の日 */
function WeekBars({ days }: { days: CampaignDetail['days'] }) {
  const { t } = useT();
  const week = useMemo(() => lastSevenDays(days, utcToday()), [days]);
  const max = Math.max(1, ...week.map((d) => d.views));
  const W = 280;
  const H = 96;
  const bw = W / 7;
  return (
    <figure className="ssa-week">
      <svg viewBox={`0 0 ${W} ${H + 18}`} role="img" aria-label={t('ssa.detail.weekLabel', { views: week.reduce((a, d) => a + d.views, 0) })}>
        {week.map((d, i) => {
          const h = d.views === 0 ? 2 : Math.max(4, Math.round((d.views / max) * H));
          return (
            <g key={d.day}>
              <rect x={i * bw + 6} y={H - h} width={bw - 12} height={h} rx={3} className={d.views === 0 ? 'ssa-bar-zero' : 'ssa-bar'}>
                <title>{`${d.day}: ${d.views}`}</title>
              </rect>
              <text x={i * bw + bw / 2} y={H + 14} textAnchor="middle" className="ssa-bar-day">
                {d.day.slice(8)}
              </text>
            </g>
          );
        })}
      </svg>
      <figcaption className="small muted">{t('ssa.detail.weekNote')}</figcaption>
    </figure>
  );
}

function LimitsForm({
  settings,
  detail,
  busy,
  onCancel,
  onSave
}: {
  settings: SelfAdSettings | null;
  detail: CampaignDetail;
  busy: boolean;
  onCancel: () => void;
  onSave: (daily: number, total: number | null) => void;
}) {
  const { t } = useT();
  const c = detail.campaign;
  const range = settings ?? FALLBACK_SETTINGS;
  const [daily, setDaily] = useState(c.dailyCapCents);
  const [total, setTotal] = useState<number | null>(c.totalCapCents);
  // 総額は使った額より下にできない（下げるとその場で終わる = 選ばせない）
  const totalOpts = totalCapOptions(daily).filter((v) => v === null || v > c.spentCents);
  const problem = capProblem(daily, total, range);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!problem) onSave(daily, total);
      }}
    >
      <div className="form-grid">
        <label className="field">
          <span>{t('ssa.budget.daily')}</span>
          <select value={daily} onChange={(e) => setDaily(Number(e.target.value))}>
            {dailyCapOptions(range).map((v) => (
              <option key={v} value={v}>
                {t('ssa.budget.option', { amount: money(v, 'usd'), views: viewsFor(v, c.priceCentsPerMille) })}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>{t('ssa.budget.total')}</span>
          <select value={total ?? ''} onChange={(e) => setTotal(e.target.value === '' ? null : Number(e.target.value))}>
            {totalOpts.map((v) => (
              <option key={v ?? 'none'} value={v ?? ''}>
                {v === null ? t('ssa.budget.noLimit') : money(v, 'usd')}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="small muted">{t('ssa.detail.limitsNote')}</p>
      {problem === 'totalBelowDaily' && <p className="field-error small">{t('ssa.err.invalid_total_cap')}</p>}
      <div className="form-actions">
        <button type="button" className="btn btn-ghost" onClick={onCancel}>
          {t('common.cancel')}
        </button>
        <button type="submit" className="btn btn-primary" disabled={busy || problem !== null}>
          {t('ssa.detail.saveLimits')}
        </button>
      </div>
    </form>
  );
}
